import { Button } from '@studio/design-system/button';
import { Calendar } from '@studio/design-system/calendar';
import { Centered } from '@studio/design-system/overlay';
import * as Select from '@studio/design-system/select';
import { dayjs } from '@studio/js-utils/time';
import { clsx } from 'cn';
import { useMemo, useState } from 'react';
import T from '@studio/design-system/themes';

/** Expiration component. */
export const Expiration = ({
  initialExpiration,
  onClose,
  onDone
}: {
  initialExpiration: Date | undefined;
  onClose: () => void;
  onDone: (expiration: Date) => void;
}) => {
  const [value, setValue] = useState<Date | undefined>(initialExpiration);

  const [timeValue, setTimeValue] = useState<string | undefined>(
    initialExpiration ? dayjs(initialExpiration).format('HH:mm') : undefined
  );

  const timeIntervals = useMemo(() => {
    const currentDay = dayjs(value);
    // Round up to the nearest 15 minutes
    const minutes = currentDay.minute();
    const roundedMinutes = 15 * Math.ceil(minutes / 15);
    let loopTime = currentDay.minute(roundedMinutes).second(0);

    // Array to store the times
    const times = [];
    const nextDay = currentDay.add(1, 'day').startOf('d');

    // Loop for 24 hours
    while (loopTime.isBefore(nextDay)) {
      // 96 intervals of 15 minutes in 24 hours
      times.push(loopTime.format('HH:mm'));
      loopTime = loopTime.add(15, 'minute');
    }

    return times;
  }, [value]);

  return (
    <Centered className='bg-black/25'>
      <div
        className={clsx('rounded-12 bg-white p-16 text-foreground', 'flex flex-col gap-12')}
        data-theme={T.getTheme('light')}
      >
        <Calendar
          onChange={(v) => {
            setValue(v);
          }}
          value={value}
        />
        <Select.Root
          key={value?.toString()}
          onValueChange={setTimeValue}
          value={timeValue}
          variant='light'
        >
          <Select.Trigger className='border-1 border-base-200 text-foreground duration-100 data-[state=open]:border-base-300'>
            <Select.Value placeholder='Select Time' />
          </Select.Trigger>

          <Select.Content
            className='max-h-[250px] overflow-auto bg-white'
            data-theme={T.getTheme('light')}
            position='popper'
            style={{
              width: 'var(--radix-popper-anchor-width)'
            }}
          >
            {timeIntervals.map((time) => (
              <Select.Item key={time} value={time}>
                {time}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <div className='mt-12 flex justify-between'>
          <Button
            onClick={() => {
              onClose();
            }}
            size='lg'
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            disabled={!value || !timeValue}
            onClick={() => {
              if (!value || !timeValue) {
                return;
              }

              const [hours, minutes] = timeValue.split(':');

              if (!hours || !minutes) {
                return;
              }

              value.setHours(Number.parseInt(hours, 10));
              value.setMinutes(Number.parseInt(minutes, 10));

              onDone(value);
            }}
            size='lg'
          >
            Set Expiration
          </Button>
        </div>
      </div>
    </Centered>
  );
};
